import { c as _c } from "react/compiler-runtime";
import { isVaultDataDecrypted, type VaultValue } from '@onefootprint/core';
import type { DataAttributeKind, DataIdentifier, EntityAttribute } from '@onefootprint/request-types/dashboard';
import type { Transforms } from '@onefootprint/types/src/data/entity';
import type { QueryClient } from '@tanstack/react-query';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { EntityVault } from '@/types/vault';
export type VaultType = {
  vault: EntityVault;
  transforms: Partial<Record<DataIdentifier, Transforms>>;
  dataKinds: Partial<Record<DataIdentifier, DataAttributeKind>>;
};
const getQueryKey = (entityId?: string, seqno?: number) => ['entity', entityId, seqno, 'vault'];

// DIs whose input components apply a Cleave mask (e.g. SSN/TIN dashes). The local cache should
// hold raw digits to match BE storage; otherwise renderers like ssnFormatter (which expects
// exactly 9 chars) silently break on post-edit values.
const MASKED_DIS: DataIdentifier[] = ['id.ssn9', 'id.ssn4', 'id.itin', 'id.us_tax_id', 'business.tin'];
const stripMaskDelimiters = (di: DataIdentifier, value: VaultValue): VaultValue => {
  if (typeof value !== 'string' || !MASKED_DIS.includes(di)) return value;
  return value.replace(/-/g, '');
};

// id.us_tax_id is the BE's umbrella DI; it auto-detects SSN vs ITIN from the first digit and
// derives the sibling on write. Mirror those derivations into the local cache so all rendered
// rows reflect the new value immediately after edit/decrypt, without waiting for a refetch.
// Unconditional by design: on edit-submit, entity.data is still the pre-edit snapshot, so we
// can't gate on it — the BE has just derived the new sibling and we need the optimistic write
// to land before invalidateQueries refetches. Phantom writes for entities truly lacking a
// sibling are harmless because downstream consumers filter by entity.data, not the vault cache.
const deriveSiblingDis = (di: DataIdentifier, value: VaultValue, target: Partial<Record<DataIdentifier, VaultValue>>): void => {
  if (typeof value !== 'string' || !value) return;
  if (di === 'id.ssn9') {
    target['id.ssn4'] = value.slice(-4);
    target['id.us_tax_id'] = value;
  } else if (di === 'id.itin') {
    target['id.us_tax_id'] = value;
  } else if (di === 'id.us_tax_id') {
    if (value.startsWith('9')) {
      target['id.itin'] = value;
    } else {
      target['id.ssn9'] = value;
      target['id.ssn4'] = value.slice(-4);
    }
  }
};

// This is a custom hook that returns the vault for an entity.
// The vault is stored in the query cache, so can persist and access from anywhere (e.g list page)
// The main reason for keeping it separated is is because we should not overwrite it once we make an entity request,
// any only when we decrypt some data we should update it.
const getVaultOrCreate = async ({
  queryClient,
  entityAttributes,
  entityId,
  seqno
}: {
  queryClient: QueryClient;
  entityAttributes: EntityAttribute[];
  entityId: string;
  seqno?: number;
}) => {
  const getFromCache = () => queryClient.getQueryData<VaultType>(getQueryKey(entityId, seqno));
  const createInitialData = (): VaultType => {
    const vaultsAndTransforms: VaultType = {
      vault: {},
      transforms: {},
      dataKinds: {}
    };
    entityAttributes.forEach(attribute => {
      // @ts-expect-error: cannot type this properly
      vaultsAndTransforms.vault[attribute.identifier] = attribute.value;
      vaultsAndTransforms.transforms[attribute.identifier] = attribute.transforms;
      vaultsAndTransforms.dataKinds[attribute.identifier] = attribute.dataKind;
    });
    return vaultsAndTransforms;
  };
  const possibleData = await getFromCache();
  return possibleData || createInitialData();
};
const useEntityVault = t0 => {
  const $ = _c(34);
  const {
    entityAttributes,
    entityId,
    seqno
  } = t0;
  const queryClient = useQueryClient();
  let t1;
  if ($[0] !== entityId || $[1] !== seqno) {
    t1 = getQueryKey(entityId, seqno);
    $[0] = entityId;
    $[1] = seqno;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const queryKey = t1;
  let t2;
  if ($[3] !== queryClient || $[4] !== queryKey) {
    t2 = newData => {
      const prevData = queryClient.getQueryData(queryKey);
      const newDataConverted = {} as Partial<Record<DataIdentifier, VaultValue>>;
      Object.keys(newData.vault).forEach(diKey => {
        const di = diKey as DataIdentifier;
        const value = stripMaskDelimiters(di, newData.vault[di]);
        newDataConverted[di] = value === null ? undefined : value;
        deriveSiblingDis(di, value, newDataConverted);
      });
      const newVault = {
        ...prevData?.vault,
        ...newDataConverted
      };
      const newTransforms = {
        ...prevData?.transforms,
        ...newData.transforms
      };
      const newDataKinds = {
        ...prevData?.dataKinds,
        ...newData.dataKinds
      };
      queryClient.setQueryData(queryKey, {
        vault: newVault,
        transforms: newTransforms,
        dataKinds: newDataKinds
      });
    };
    $[3] = queryClient;
    $[4] = queryKey;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const update = t2;
  let t3;
  if ($[6] !== entityAttributes) {
    t3 = data => {
      if (!entityAttributes || !data) {
        return false;
      }
      const relevantDis = ["id.first_name", "id.middle_name", "id.last_name", "id.email", "id.phone_number", "id.address_line1", "id.address_line2", "id.city", "id.state", "id.zip", "id.country", "id.dob", "id.ssn4", "id.ssn9"];
      const decryptableDIs = entityAttributes.filter(_temp).map(_temp2).filter(di_0 => relevantDis.some(relevantDi => di_0.startsWith(relevantDi)));
      return decryptableDIs.every(di_1 => isVaultDataDecrypted(data.vault[di_1]));
    };
    $[6] = entityAttributes;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const isIdDecrypted = t3;
  let t4;
  if ($[8] !== entityAttributes) {
    t4 = data_0 => {
      if (!entityAttributes || !data_0) {
        return false;
      }
      const decryptableDIs_0 = entityAttributes.filter(_temp3).map(_temp4);
      const nonDocumentDIs = decryptableDIs_0.filter(_temp5);
      return nonDocumentDIs.every(di_3 => {
        const value_0 = data_0.vault[di_3];
        return isVaultDataDecrypted(value_0);
      });
    };
    $[8] = entityAttributes;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const isAllNonDocumentDecrypted = t4;
  let t5;
  if ($[10] !== entityAttributes) {
    t5 = data_1 => {
      if (!entityAttributes || !data_1) {
        return false;
      }
      const decryptableDIs_1 = entityAttributes.filter(_temp6).map(_temp7);
      return decryptableDIs_1.every(di_4 => {
        const value_1 = data_1.vault[di_4];
        return isVaultDataDecrypted(value_1);
      });
    };
    $[10] = entityAttributes;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const isAllDecrypted = t5;
  let t6;
  if ($[12] !== entityAttributes) {
    t6 = di_5 => entityAttributes?.some(attr_5 => attr_5.isDecryptable && attr_5.identifier === di_5);
    $[12] = entityAttributes;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const isDecryptable = t6;
  const isDecrypted = _temp8;
  const hasDi = di_7 => query.data?.vault[di_7] !== undefined;
  let t7;
  if ($[14] !== entityAttributes || $[15] !== entityId || $[16] !== queryClient || $[17] !== seqno) {
    t7 = () => getVaultOrCreate({
      queryClient,
      entityAttributes: entityAttributes ?? [],
      entityId: entityId ?? "",
      seqno
    });
    $[14] = entityAttributes;
    $[15] = entityId;
    $[16] = queryClient;
    $[17] = seqno;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  const t8 = !!entityId && !!entityAttributes;
  let t9;
  if ($[19] !== queryKey || $[20] !== t7 || $[21] !== t8) {
    t9 = {
      queryKey,
      queryFn: t7,
      enabled: t8,
      staleTime: Number.POSITIVE_INFINITY
    };
    $[19] = queryKey;
    $[20] = t7;
    $[21] = t8;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  const query = useQuery(t9);
  const t10 = query;
  const t11 = isAllDecrypted(query.data);
  const t12 = isIdDecrypted(query.data);
  const t13 = isAllNonDocumentDecrypted(query.data);
  let t14;
  if ($[23] !== query) {
    t14 = di_8 => isDecrypted(query.data, di_8);
    $[23] = query;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  let t15;
  if ($[25] !== hasDi || $[26] !== isDecryptable || $[27] !== t10 || $[28] !== t11 || $[29] !== t12 || $[30] !== t13 || $[31] !== t14 || $[32] !== update) {
    t15 = {
      ...t10,
      update,
      isAllDecrypted: t11,
      isIdDecrypted: t12,
      isAllNonDocumentDecrypted: t13,
      isDecryptable,
      isDecrypted: t14,
      hasDi
    };
    $[25] = hasDi;
    $[26] = isDecryptable;
    $[27] = t10;
    $[28] = t11;
    $[29] = t12;
    $[30] = t13;
    $[31] = t14;
    $[32] = update;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  return t15;
};
export default useEntityVault;
function _temp(attr) {
  return attr.isDecryptable;
}
function _temp2(attr_0) {
  return attr_0.identifier;
}
function _temp3(attr_1) {
  return attr_1.isDecryptable;
}
function _temp4(attr_2) {
  return attr_2.identifier;
}
function _temp5(di_2) {
  return !di_2.startsWith("document");
}
function _temp6(attr_3) {
  return attr_3.isDecryptable;
}
function _temp7(attr_4) {
  return attr_4.identifier;
}
function _temp8(data_2, di_6) {
  return isVaultDataDecrypted(data_2?.vault[di_6]);
}